import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Heart, Shield, Award } from 'lucide-react';
import GlassCard from '../UI/GlassCard';

interface FoodItem {
  id: string;
  name: string;
  confidence: number;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
    sodium: number;
    vitamins: { [key: string]: number };
    minerals: { [key: string]: number };
  };
  allergens: string[];
  dietaryTags: string[];
  healthScore: number;
  boundingBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface NutritionOverlayProps {
  foods: FoodItem[];
  imageRef?: React.RefObject<HTMLImageElement>;
}

const NutritionOverlay: React.FC<NutritionOverlayProps> = ({ foods, imageRef }) => {
  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getHealthScoreIcon = (score: number) => {
    if (score >= 80) return <Heart className="w-5 h-5" />;
    if (score >= 60) return <Shield className="w-5 h-5" />;
    return <Zap className="w-5 h-5" />;
  };

  return (
    <div className="space-y-4">
      {foods.map((food, index) => (
        <motion.div
          key={food.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.2 }}
        >
          <GlassCard className="p-6" glow>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-1">{food.name}</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-purple-300 text-sm">
                    {Math.round(food.confidence * 100)}% confidence
                  </span>
                  <div className={`flex items-center space-x-1 ${getHealthScoreColor(food.healthScore)}`}>
                    {getHealthScoreIcon(food.healthScore)}
                    <span className="font-semibold">{food.healthScore}/100</span>
                  </div>
                </div>
              </div>
              <Award className="w-6 h-6 text-yellow-400" />
            </div>

            {/* Macronutrients */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{food.nutrition.calories}</div>
                <div className="text-purple-300 text-sm">Calories</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{food.nutrition.protein}g</div>
                <div className="text-purple-300 text-sm">Protein</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{food.nutrition.carbs}g</div>
                <div className="text-purple-300 text-sm">Carbs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{food.nutrition.fat}g</div>
                <div className="text-purple-300 text-sm">Fat</div>
              </div>
            </div>

            {/* Vitamins & Minerals */}
            {(Object.keys(food.nutrition.vitamins).length > 0 || Object.keys(food.nutrition.minerals).length > 0) && (
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3">Key Nutrients</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(food.nutrition.vitamins).map(([vitamin, value]) => (
                    <div key={vitamin} className="flex justify-between text-sm">
                      <span className="text-purple-300">{vitamin}</span>
                      <span className="text-white">{value}% DV</span>
                    </div>
                  ))}
                  {Object.entries(food.nutrition.minerals).map(([mineral, value]) => (
                    <div key={mineral} className="flex justify-between text-sm">
                      <span className="text-purple-300">{mineral}</span>
                      <span className="text-white">{value}% DV</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dietary Tags */}
            {food.dietaryTags.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {food.dietaryTags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm border border-green-500/30"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Allergens */}
            {food.allergens.length > 0 && (
              <div className="mb-4">
                <h4 className="text-red-300 font-semibold mb-2">⚠️ Allergens</h4>
                <div className="flex flex-wrap gap-2">
                  {food.allergens.map((allergen) => (
                    <span
                      key={allergen}
                      className="bg-red-500/20 text-red-300 px-3 py-1 rounded-full text-sm border border-red-500/30"
                    >
                      {allergen}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Health Score Bar */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-semibold">Health Score</span>
                <span className={`font-bold ${getHealthScoreColor(food.healthScore)}`}>
                  {food.healthScore}/100
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <motion.div
                  className={`h-3 rounded-full ${
                    food.healthScore >= 80 ? 'bg-green-500' :
                    food.healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${food.healthScore}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
            </div>
          </GlassCard>
        </motion.div>
      ))}
    </div>
  );
};

export default NutritionOverlay;